import { useEffect, useMemo, useState, useRef } from "react";
import { api } from "@/lib/api";
import { Sliders, RotateCcw, Loader2 } from "lucide-react";
import { SensitivityChart } from "@/components/SensitivityChart";
import { useT } from "@/lib/i18n";
import { comparisonValue } from "@/lib/comparison";
import { fuelAxis, hasSolar } from "@/lib/sensitivityFuel";
import { num, money, moneyM, pct, unit, perUnit, perYr } from "@/lib/numfmt";

const fmt = (n, d = 3) => num(n || 0, d);
const usd = (n) => `${money(n || 0, 0)}`;

/**
 * Sensitivity control: two sliders (fuel_price and irradiance) that debounce-recompute
 * against POST /api/configurator/run. Baseline pinned; live delta displayed.
 */
export const SensitivityPanel = ({ baselineInput, baselineResult }) => {
  const t = useT();
  // Prompt K §8.7 — a gas plant is flexed on the gas price, not on diesel.
  const axis = useMemo(() => fuelAxis(baselineInput, baselineResult), [baselineInput, baselineResult]);
  const baselineFuel = axis.baseline;
  const baselineIrr = baselineInput?.irradiance ?? 5.5;
  const solar = hasSolar(baselineResult);

  const [fuel, setFuel] = useState(baselineFuel);
  const [irr, setIrr] = useState(baselineIrr);
  const [live, setLive] = useState({
    lcoe: baselineResult?.financials?.lcoe_usd_per_kwh,
    capex: baselineResult?.capex?.total,
    opex: baselineResult?.opex?.annual,
    payback: comparisonValue(baselineResult?.financials?.simple_payback_years),
  });
  const [computing, setComputing] = useState(false);
  const timer = useRef(null);
  const seqRef = useRef(0);

  useEffect(() => {
    if (fuel === baselineFuel && irr === baselineIrr) {
      setLive({
        lcoe: baselineResult?.financials?.lcoe_usd_per_kwh,
        capex: baselineResult?.capex?.total,
        opex: baselineResult?.opex?.annual,
        payback: comparisonValue(baselineResult?.financials?.simple_payback_years),
      });
      return;
    }
    if (timer.current) clearTimeout(timer.current);
    setComputing(true);
    const mySeq = ++seqRef.current;
    timer.current = setTimeout(async () => {
      try {
        const patched = { ...axis.patch(baselineInput, fuel), irradiance: irr };
        const r = await api.post("/configurator/run", patched);
        if (mySeq !== seqRef.current) return; // stale
        setLive({
          lcoe: r.data.financials.lcoe_usd_per_kwh,
          capex: r.data.capex.total,
          opex: r.data.opex.annual,
          payback: comparisonValue(r.data.financials.simple_payback_years),
        });
      } catch (err) {
        console.warn("[sensitivity] recompute failed", err?.message);
      } finally {
        if (mySeq === seqRef.current) setComputing(false);
      }
    }, 350);
    return () => timer.current && clearTimeout(timer.current);
  }, [fuel, irr, baselineFuel, baselineIrr, baselineInput, baselineResult, axis]);

  const reset = () => { setFuel(baselineFuel); setIrr(baselineIrr); };

  const lcoeBase = baselineResult?.financials?.lcoe_usd_per_kwh || 0;
  const lcoeDelta = ((live.lcoe || 0) - lcoeBase);
  const lcoePct = lcoeBase ? (lcoeDelta / lcoeBase) * 100 : 0;
  const worse = lcoeDelta > 0.0005;
  const better = lcoeDelta < -0.0005;
  const deltaColor = worse ? "var(--orange-spark)" : better ? "var(--brand-green)" : "var(--muted)";

  return (
    <section className="card p-6 md:p-8" data-testid="sensitivity-panel">
      <div className="flex flex-wrap justify-between items-start gap-4 mb-6">
        <div className="flex items-center gap-3">
          <Sliders size={18} style={{ color: "var(--brand-green)" }} />
          <div>
            <div className="overline">{t("sens.overline")}</div>
            <h3 className="font-display text-xl mt-1">{t(solar ? "sens.title" : "sens.title.fuel_only")}</h3>
            <p className="text-sm mt-1" style={{ color: "var(--muted)" }}>{t("sens.desc")}</p>
          </div>
        </div>
        {(fuel !== baselineFuel || irr !== baselineIrr) && (
          <button onClick={reset} className="btn-ghost !p-0" data-testid="sensitivity-reset">
            <RotateCcw size={12} className="inline" /> {t("sens.reset")}
          </button>
        )}
      </div>

      <div className={`grid grid-cols-1 ${solar ? "lg:grid-cols-2" : ""} gap-6`}>
        <SliderRow
          label={t(axis.labelKey)}
          value={fuel}
          setValue={setFuel}
          min={axis.min} max={axis.max} step={axis.step}
          baseline={baselineFuel}
          baselineLabel={t("sens.baseline")}
          testid="sensitivity-fuel"
        />
        {solar && <SliderRow
          label={t("sens.irradiance")}
          value={irr}
          setValue={setIrr}
          min={3.0} max={7.5} step={0.1}
          baseline={baselineIrr}
          baselineDigits={1}
          baselineLabel={t("sens.baseline")}
          testid="sensitivity-irradiance"
        />}
      </div>

      {/* Live outputs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-8">
        <LiveTile label={t("sens.tile.lcoe")} value={perUnit(live.lcoe || 0, "kWh", 4)} sub={
          <span style={{ color: deltaColor }}>
            {computing ? <Loader2 size={11} className="inline animate-spin" /> : (
              (lcoeDelta === 0 ? t("sens.baseline") : `${lcoeDelta >= 0 ? "+" : ""}${fmt(lcoeDelta, 4)}  (${lcoePct >= 0 ? "+" : ""}${pct(lcoePct, 1)})`)
            )}
          </span>
        } testid="live-lcoe" />
        <LiveTile label={t("sens.tile.capex")} value={usd(live.capex)} testid="live-capex" />
        <LiveTile label={t("sens.tile.opex")} value={usd(live.opex)} testid="live-opex" />
        <LiveTile label={t("sens.tile.payback")} value={live.payback ? `${num(live.payback, 2)} ${t("sens.tile.payback_yr")}` : "—"} testid="live-payback" />
      </div>

      {/* Sweep chart */}
      <div className="mt-8">
        <SensitivityChart baselineInput={baselineInput} baselineResult={baselineResult} currentFuel={fuel} currentIrr={irr} />
      </div>
    </section>
  );
};

const SliderRow = ({ label, value, setValue, min, max, step, baseline, baselineDigits = 0, baselineLabel, testid }) => {
  const pct = ((value - min) / (max - min)) * 100;
  return (
    <div>
      <div className="flex justify-between items-baseline mb-2">
        <label className="label-field !mb-0">{label}</label>
        <span className="font-mono text-sm" style={{ color: "var(--charcoal)" }}>{num(value, 2, 2)}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => setValue(parseFloat(e.target.value))}
        className="w-full accent-[var(--brand-green)]"
        style={{ background: `linear-gradient(to right, var(--brand-green) ${pct}%, var(--hairline) ${pct}%)` }}
        data-testid={testid}
      />
      <div className="flex justify-between text-xs font-mono mt-1" style={{ color: "var(--muted)" }}>
        <span>{num(min, 0, 0)}</span>
        <span>{baselineLabel || "baseline"} {num(baseline, baselineDigits, baselineDigits)}</span>
        <span>{num(max, 0, 0)}</span>
      </div>
    </div>
  );
};

const LiveTile = ({ label, value, sub, testid }) => (
  <div className="p-4 rounded-md" style={{ background: "var(--paper)" }} data-testid={testid}>
    <div className="overline" style={{ fontSize: "0.6rem" }}>{label}</div>
    <div className="font-display text-lg mt-1" style={{ color: "var(--charcoal)" }}>{value}</div>
    {sub && <div className="text-xs font-mono mt-1">{sub}</div>}
  </div>
);
